import { PopupOnClick } from '../../general_components/content_management/reusableItems/itemOnPopup';
import { extractStateStr } from '../../main_board_components/unassignedLoadsTable';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import styles from './reports.module.scss';
import { resolveVisibleCatalog } from './reportColumnSettings';

const dash = '-';

const formatMiles = (value) => {
    if (value == null || value === '') return dash;
    const num = Number(value);
    if (!Number.isFinite(num)) return dash;
    return num.toLocaleString('en-US');
};

export const computeLoadRpm = (amount, miles) => {
    const rate = Number(amount);
    const distance = Number(miles);
    if (!Number.isFinite(rate) || !Number.isFinite(distance) || distance <= 0) return null;
    return rate / distance;
};

export const formatLoadRpm = (amount, miles) => {
    const rpm = computeLoadRpm(amount, miles);
    if (rpm == null) return dash;
    return priceOnFinish(rpm.toFixed(2));
};

export const formatDriverPayoutLines = (lines) => {
    if (!Array.isArray(lines) || lines.length === 0) return dash;
    const setLines = lines.filter((line) => line?.amount != null && line.amount !== '');
    if (setLines.length === 0) return dash;
    const money = (amount) => priceOnFinish(`${amount}`, true);
    if (setLines.length === 1) return money(setLines[0].amount);
    return setLines
        .map((line) => {
            const truck = line?.truckNumber ? `#${line.truckNumber}` : '#';
            return `${truck}: ${money(line.amount)}`;
        })
        .join('; ');
};

const formatLocation = (load) => {
    const start = load?.startCityState;
    const end = load?.endCityState;
    if (!start && !end) return dash;
    return `${start || dash} - ${end || dash}`;
};

const formatStates = (load) => {
    const start = extractStateStr(load?.startCityState);
    const end = extractStateStr(load?.endCityState);
    return `${start}-${end}`;
};

export const getReportLoadCellValue = (load, column) => {
    if (!load || !column) return dash;
    switch (column.type) {
        case 'loadLink':
            return load.loadNumber || dash;
        case 'location':
            return formatLocation(load);
        case 'states':
            return formatStates(load);
        case 'miles':
            return formatMiles(load[column.key]);
        case 'rpm':
            return formatLoadRpm(load.amountForInvoice, load[column.milesKey]);
        case 'driverPayout':
            return formatDriverPayoutLines(load.driverPayouts);
        case 'money': {
            if (load[column.key] == null || load[column.key] === '') return dash;
            return priceOnFinish(`${load[column.key]}`, true);
        }
        case 'text':
        default: {
            const value = load[column.key];
            if (value == null || value === '') return dash;
            return String(value);
        }
    }
};

export const getReportLoadExportValue = (load, column) => {
    if (!load || !column) return '';
    switch (column.type) {
        case 'miles': {
            const num = Number(load[column.key]);
            return Number.isFinite(num) ? num : '';
        }
        case 'rpm': {
            const rpm = computeLoadRpm(load.amountForInvoice, load[column.milesKey]);
            return rpm == null ? '' : Number(rpm.toFixed(2));
        }
        case 'money': {
            const num = Number(load[column.key]);
            return Number.isFinite(num) ? num : '';
        }
        case 'driverPayout': {
            const text = formatDriverPayoutLines(load.driverPayouts);
            return text === dash ? '' : text;
        }
        default: {
            const text = getReportLoadCellValue(load, column);
            return text === dash ? '' : text;
        }
    }
};

const ReportLoadCell = ({ load, column }) => {
    const alignRight = column.align === 'right' || column.pin === 'end';
    const cellClass = alignRight ? styles.loadSubCellRight : undefined;
    if (column.type === 'loadLink') {
        return (
            <div className={cellClass} onClick={(e) => e.stopPropagation()}>
                <PopupOnClick text={load.loadNumber} itemType="load" itemId={load.id} />
            </div>
        );
    }
    const value = getReportLoadCellValue(load, column);
    return <div className={cellClass} title={value}>{value}</div>;
};

export const ReportLoadSubContent = ({
    currentReport,
    visibleColumns,
    catalog,
    loadsKey = 'loads',
}) => {
    const loads = Array.isArray(currentReport?.[loadsKey]) ? currentReport[loadsKey] : [];
    const columns = resolveVisibleCatalog(visibleColumns, catalog);
    if (columns.length === 0) return null;

    const template = columns.map((col) => col.grid || 'minmax(6rem, 1fr)').join(' ');

    return (
        <div className={styles.loadSubTable} style={{ '--sub-col-template': template }}>
            <div className={styles.loadSubHeader}>
                {columns.map((col) => (
                    <div
                        key={col.key}
                        className={col.align === 'right' || col.pin === 'end' ? styles.loadSubCellRight : undefined}
                    >
                        {col.label}
                    </div>
                ))}
            </div>
            {loads.map((load, index) => (
                <div key={load?.id ?? index} className={styles.loadSubRow}>
                    {columns.map((col) => (
                        <ReportLoadCell key={col.key} load={load} column={col} />
                    ))}
                </div>
            ))}
        </div>
    );
};
